/**
 * features/exams/InternalExams.tsx
 * ------------------------------------------------------------------
 * Exams → Internal exams (tab `internalExams`, gate canManageExamTimetables:
 * the exams officer). LKC, 30 Oct 2026, from Al-Nur's screen of the same name.
 *
 * Al-Nur's office uploads a workbook. LKC's Examinations Unit publishes PDFs,
 * and the school chose to load those once: the End of Year 2026 timetable is
 * a file in src/content/examTimetables/, already matched to portal subjects
 * (lib/internalExams.ts). So this screen does not read a file — it shows that
 * timetable against today's pupil records, form by form, flags every paper
 * where the portal's pupils and the Unit's candidate count part, lists what
 * would change against what is already published, and writes it.
 *
 * Publishing writes the shapes the Cambridge series already uses, so every
 * Exams screen and the pupil's and parent's timetable work on it unchanged:
 * the series (`reportExamSeries`), a paper per `reportExamDuties` doc (its
 * duties are given on Exams → Invigilation), a register per paper
 * (`reportExamRegisters`, one for every form sitting that code in that
 * session), and a `reportExamEntries` statement per pupil, built from their
 * enrolments.
 * Candidate numbers are not issued: the Unit's PDFs carry none.
 *
 * Re-publishing keeps any duties already on a paper and any register's marks
 * and signature, and removes statements for pupils who no longer sit anything
 * (a subject dropped, a pupil left).
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2 } from 'lucide-react';
import { db } from '@/lib/firebase';
import { stripUndefined } from '@/lib/sanitize';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import {
  buildUnitDocs, changesBetween, countGaps, sessionTimesOf,
  type InternalPaperDoc, type SittingPupil, type UnitTimetable,
} from '@/lib/internalExams';
import { SESSION_NAMES, type ExamSeries } from '@/lib/examTimetable';
import { Page, PageHeader, SoftCard, AccentPanel, Tabs, StatChip, StatStrip, glassFieldClass, btnPrimary, btnGhost } from '@/components/ui/design';

/** Every timetable the Examinations Unit has given us, lazily. */
const TIMETABLES: Record<string, { label: string; load: () => Promise<UnitTimetable> }> = {
  'lkc-eoy-2026': {
    label: 'End of Year Examinations, November 2026',
    load: () => import('@/content/examTimetables/lkc-eoy-2026.json').then((m) => m.default as UnitTimetable),
  },
};

const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export default function InternalExams() {
  return (
    <Page wide>
      <InternalExamsBody />
    </Page>
  );
}

function InternalExamsBody() {
  const { userData, permissions } = useAuth();
  const { activeStudents, classes } = useData();
  const [ttId, setTtId] = useState(Object.keys(TIMETABLES)[0]);
  const [tt, setTt] = useState<UnitTimetable | null>(null);
  const [existing, setExisting] = useState<ExamSeries | null>(null);
  const [before, setBefore] = useState<InternalPaperDoc[]>([]);
  const [title, setTitle] = useState('');
  const [tab, setTab] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const seriesId = ttId;

  useEffect(() => {
    let live = true;
    void TIMETABLES[ttId].load().then((t) => { if (live) { setTt(t); setTitle((x) => x || t.title); } });
    void getDocs(query(collection(db, 'reportExamSeries'), where('kind', '==', 'internal'))).then((s) => {
      if (!live) return;
      const found = s.docs.find((d) => d.id === seriesId);
      setExisting(found ? ({ ...(found.data() as ExamSeries), id: found.id }) : null);
      if (found) setTitle((found.data() as ExamSeries).title);
    }).catch(() => setExisting(null));
    void getDocs(query(collection(db, 'reportExamDuties'), where('seriesId', '==', seriesId)))
      .then((s) => { if (live) setBefore(s.docs.map((d) => ({ ...(d.data() as InternalPaperDoc), id: d.id }))); })
      .catch(() => setBefore([]));
    return () => { live = false; };
  }, [ttId, seriesId, refresh]);

  /* The pupils, with their form as a number. Only Forms 1–9 sit an internal
     series; sixth form and pre-school classes have a non-numeric form. */
  const formOfClass = useMemo(() => new Map(classes.map((c) => [c.id, c.form])), [classes]);
  const classNameOf = useMemo(() => new Map(classes.map((c) => [c.id, c.name])), [classes]);
  const pupils = useMemo<SittingPupil[]>(() => activeStudents
    .map((s) => ({
      id: s.id, fullName: s.fullName, className: classNameOf.get(s.currentClass) ?? '',
      form: Number(formOfClass.get(s.currentClass)), subjects: s.subjects ?? [],
    }))
    .filter((p) => Number.isInteger(p.form) && p.form >= 1 && p.form <= 9), [activeStudents, formOfClass, classNameOf]);

  const built = useMemo(() => (tt ? buildUnitDocs(seriesId, tt, pupils, {}) : null), [tt, seriesId, pupils]);
  const gaps = useMemo(() => (built ? countGaps(built.papers) : []), [built]);
  const changes = useMemo(() => (built && before.length ? changesBetween(before, built.papers) : null), [built, before]);
  const forms = useMemo(() => [...new Set((tt?.papers ?? []).map((p) => p.form))].sort(), [tt]);
  useEffect(() => { if (!tab && forms.length) setTab(String(forms[0])); }, [forms, tab]);

  async function publish(publishIt: boolean) {
    if (!built || !tt) return;
    setBusy(true); setNote(null);
    try {
      const now = new Date().toISOString();
      const firstDate = built.papers.map((p) => p.date).sort()[0];
      const countdown = new Date(new Date(`${firstDate}T12:00:00`).getTime() - 28 * 86400000).toISOString().slice(0, 10);
      const formsSat = new Set(forms.map(String));
      const seriesDoc = {
        id: seriesId, kind: 'internal', title: title.trim() || tt.title, timetableId: '', published: publishIt,
        countdownFrom: existing?.countdownFrom ?? countdown,
        sessions: sessionTimesOf(tt),
        sessionLabels: SESSION_NAMES,
        classes: classes.filter((c) => formsSat.has(String(c.form))).map((c) => ({ className: c.name, level: 'School' })),
        subjects: [],
        source: { fileName: tt.source, uploadedAt: now, uploadedBy: userData?.displayName ?? '' },
        updatedAt: now, updatedBy: userData?.uid ?? '', updatedByName: userData?.displayName ?? '',
      };
      const [dutySnap, regSnap, entrySnap] = await Promise.all([
        getDocs(query(collection(db, 'reportExamDuties'), where('seriesId', '==', seriesId))),
        getDocs(query(collection(db, 'reportExamRegisters'), where('seriesId', '==', seriesId))),
        getDocs(query(collection(db, 'reportExamEntries'), where('seriesId', '==', seriesId))),
      ]);
      const oldDuties = new Map(dutySnap.docs.map((d) => [d.id, (d.data().duties ?? []) as { teacherId: string }[]]));
      const oldRegs = new Map(regSnap.docs.map((d) => [d.id, d.data()]));
      type W = { ref: ReturnType<typeof doc>; data?: Record<string, unknown>; del?: boolean; merge?: boolean };
      const writes: W[] = [{ ref: doc(db, 'reportExamSeries', seriesId), data: seriesDoc }];
      const keep = new Set(built.papers.map((p) => p.id));
      for (const p of built.papers) {
        writes.push({ ref: doc(db, 'reportExamDuties', p.id), data: { ...p, duties: oldDuties.get(p.id) ?? [], source: tt.source, updatedAt: now } });
      }
      for (const d of dutySnap.docs) if (!keep.has(d.id)) writes.push({ ref: d.ref, del: true });
      // A register per paper per session (every form sitting that code then):
      // created fresh, or its details refreshed with marks and signature kept.
      const regIds = new Set<string>();
      for (const p of built.papers) {
        const rid = `${seriesId}__${p.date}__${p.session}__${p.code.replace(/\//g, '-')}`;
        if (regIds.has(rid)) continue;
        regIds.add(rid);
        const same = built.papers.filter((x) => x.code === p.code && x.date === p.date && x.session === p.session);
        const invigilatorIds = [...new Set(same.flatMap((x) => (oldDuties.get(x.id) ?? []).map((d) => d.teacherId)).filter(Boolean))];
        const fields = {
          id: rid, seriesId, code: p.code, date: p.date, session: p.session, subject: p.subject, component: p.component,
          start: p.start, end: p.end, officeCount: same.reduce((n, x) => n + x.entries, 0), invigilatorIds, noRota: false,
          updatedAt: now, updatedBy: userData?.uid ?? '',
        };
        writes.push(oldRegs.has(rid) ? { ref: doc(db, 'reportExamRegisters', rid), data: fields, merge: true }
          : { ref: doc(db, 'reportExamRegisters', rid), data: { ...fields, marks: {} } });
      }
      for (const [rid, r] of oldRegs) {
        if (!regIds.has(rid) && !(r.confirmedAt || Object.keys(r.marks ?? {}).length)) writes.push({ ref: doc(db, 'reportExamRegisters', rid), del: true });
      }
      const sitting = new Set(built.entries.map((e) => e.studentId));
      for (const e of built.entries) {
        writes.push({ ref: doc(db, 'reportExamEntries', `${seriesId}_${e.studentId}`), data: { ...e, importedAt: now } as unknown as Record<string, unknown> });
      }
      for (const d of entrySnap.docs) if (!sitting.has(String(d.data().studentId))) writes.push({ ref: d.ref, del: true });

      for (let i = 0; i < writes.length; i += 400) {
        const batch = writeBatch(db);
        for (const w of writes.slice(i, i + 400)) {
          if (w.del) batch.delete(w.ref);
          else batch.set(w.ref, stripUndefined(w.data!) as Record<string, unknown>, w.merge ? { merge: true } : {});
        }
        await batch.commit();
      }
      setNote({ ok: true, text: publishIt
        ? `Published: ${built.papers.length} papers for ${built.entries.length} pupils. Pupils and parents see their papers on their Exams tab.`
        : `Saved as a draft: ${built.papers.length} papers. Staff see it on Exams → Timetable; pupils and parents do not until it is published.` });
      setRefresh((n) => n + 1);
    } catch (e) {
      setNote({ ok: false, text: `Nothing was published: ${(e as Error).message}` });
    } finally { setBusy(false); }
  }

  async function setPublished(published: boolean) {
    if (!existing) return;
    setBusy(true); setNote(null);
    try {
      const batch = writeBatch(db);
      batch.set(doc(db, 'reportExamSeries', existing.id), { published, updatedAt: new Date().toISOString() }, { merge: true });
      await batch.commit();
      setRefresh((n) => n + 1);
      setNote({ ok: true, text: published ? `${existing.title} is published.` : `${existing.title} is hidden from pupils and parents again.` });
    } catch (e) { setNote({ ok: false, text: (e as Error).message }); } finally { setBusy(false); }
  }

  if (!permissions.canManageExamTimetables) {
    return <SoftCard><p className="text-sm text-[var(--sp-body)]">Internal exam timetables are set up by the exams officer. The published timetable is on Exams → Timetable.</p></SoftCard>;
  }

  const formPapers = (built?.papers ?? []).filter((p) => String(p.form) === tab)
    .sort((a, b) => a.date.localeCompare(b.date) || a.session.localeCompare(b.session) || a.start.localeCompare(b.start));
  const gapIds = new Set(gaps.map((g) => g.id));

  return (
    <>
      <PageHeader icon={CalendarClock} eyebrow="Exams" title="Internal exams"
        subtitle="The school's own exams, from the Examinations Unit's timetable" />

      <SoftCard className="mb-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs font-medium text-[var(--sp-muted)]">Timetable
            <select className={glassFieldClass()} value={ttId} onChange={(e) => { setTtId(e.target.value); setTt(null); setTitle(''); }}>
              {Object.entries(TIMETABLES).map(([id, t]) => <option key={id} value={id}>{t.label}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-[var(--sp-muted)]">Title pupils and parents see
            <input className={glassFieldClass()} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
        </div>
        {tt && <p className="text-xs text-[var(--sp-muted)]">From {tt.source}, read on {tt.extracted}.</p>}
        {existing && (
          <p className="flex flex-wrap items-center gap-2 text-sm">
            {existing.published
              ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Published — pupils and parents can see it.</>
              : <><AlertTriangle className="h-4 w-4 text-amber-600" /> Saved as a draft — staff only.</>}
            <button type="button" className={btnGhost} disabled={busy} onClick={() => void setPublished(!existing.published)}>
              {existing.published ? 'Hide from pupils and parents' : 'Publish as it stands'}
            </button>
          </p>
        )}
      </SoftCard>

      {!built ? (
        <p className="flex items-center gap-2 py-10 text-sm text-[var(--sp-muted)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading the timetable…</p>
      ) : (
        <>
          <StatStrip>
            <StatChip label="Papers" value={built.papers.length} />
            <StatChip label="Pupils sitting" value={built.entries.length} />
            <StatChip label="Forms" value={forms.join(', ')} />
            <StatChip label="Counts to check" value={gaps.length} />
          </StatStrip>

          {gaps.length > 0 && (
            <AccentPanel tone="gold" className="my-4">
              <p className="text-sm font-semibold text-ink dark:text-slate-100">
                {gaps.length} paper{gaps.length === 1 ? '' : 's'} where the portal's pupils and the Examinations Unit's count part
              </p>
              <p className="mt-1 text-xs text-[var(--sp-body)]">
                A pupil sits a paper when the subject is on their record. Where the portal has fewer, some pupils' subjects are
                missing and they will not see that paper; correct their subjects on their profile, then publish again.
              </p>
              <ul className="mt-2 space-y-0.5 text-xs text-[var(--sp-body)]">
                {gaps.map((g) => (
                  <li key={g.id}>Form {g.form} · {shortDate(g.date)} · {g.code} {g.subject} — portal {g.entries}, Unit {g.officeCount}</li>
                ))}
              </ul>
            </AccentPanel>
          )}

          {changes && (changes.added.length + changes.removed.length + changes.moved.length > 0) && (
            <SoftCard className="my-4 text-sm">
              <p className="font-semibold text-ink dark:text-slate-100">Against what is saved now</p>
              <ul className="mt-1 space-y-0.5 text-xs text-[var(--sp-body)]">
                {changes.moved.map((m) => <li key={`m${m.to.id}`}>Moved: Form {m.to.form} {m.to.code} {m.to.subject}, {shortDate(m.from.date)} {m.from.start} → {shortDate(m.to.date)} {m.to.start}</li>)}
                {changes.added.map((p) => <li key={`a${p.id}`}>New: Form {p.form} {p.code} {p.subject}, {shortDate(p.date)} {p.start}</li>)}
                {changes.removed.map((p) => <li key={`r${p.id}`}>Gone: Form {p.form} {p.code} {p.subject}, {shortDate(p.date)} {p.start}</li>)}
              </ul>
            </SoftCard>
          )}

          <div className="my-4">
            <Tabs value={tab} onChange={setTab} items={forms.map((f) => ({ id: String(f), label: `Form ${f}` }))} />
          </div>

          <SoftCard pad="p-0" className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-[var(--sp-muted)]">
                <tr><th className="px-4 py-2">Day</th><th className="px-4 py-2">Session</th><th className="px-4 py-2">Time</th>
                  <th className="px-4 py-2">Paper</th><th className="px-4 py-2">Room</th>
                  <th className="px-4 py-2 text-right">Portal</th><th className="px-4 py-2 text-right">Unit</th></tr>
              </thead>
              <tbody className="divide-y divide-[var(--sp-line)]">
                {formPapers.map((p) => (
                  <tr key={p.id} className={gapIds.has(p.id) ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''}>
                    <td className="px-4 py-2 whitespace-nowrap">{shortDate(p.date)}</td>
                    <td className="px-4 py-2">{SESSION_NAMES[p.session]}</td>
                    <td className="px-4 py-2 tabular-nums whitespace-nowrap">{p.start}–{p.end}</td>
                    <td className="px-4 py-2"><span className="font-semibold tabular-nums">{p.code}</span> {p.subject}
                      {p.component && <span className="block text-xs text-[var(--sp-muted)]">{p.component} · {p.duration}</span>}</td>
                    <td className="px-4 py-2 text-xs">{p.room}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{p.entries}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[var(--sp-muted)]">{p.officeCount ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SoftCard>

          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            {note && <p className={`mr-auto text-sm ${note.ok ? 'text-emerald-700' : 'text-rose-600'}`}>{note.text}</p>}
            <button type="button" className={btnGhost} disabled={busy} onClick={() => void publish(false)}>Save as draft</button>
            <button type="button" className={btnPrimary} disabled={busy} onClick={() => void publish(true)}>
              {busy ? 'Writing…' : existing?.published ? 'Publish again' : 'Publish to pupils and parents'}
            </button>
          </div>
        </>
      )}
    </>
  );
}
